import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Loader,
  Button,
  Badge,
} from "@mantine/core";
import {
  IconSettings,
  IconCalendar,
  IconDownload,
  IconRefresh,
  IconClock,
  IconUsers,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface OperationalSummary {
  totalLoanRequests: number;
  approvedCount: number;
  rejectedCount: number;
  pendingCount: number;
  approvalRate: number;
  rejectionRate: number;
  averageProcessingTimeHours: number;
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  deactivatedUsers: number;
  userActivityRate: number;
}

interface RequestByDay {
  _id: string;
  count: number;
  approved: number;
  rejected: number;
  pending: number;
}

interface RequestByProduct {
  _id: string;
  count: number;
  approved: number;
  rejected: number;
  totalAmount: number;
}

interface OperationalResponse {
  summary: OperationalSummary;
  requestsByDay: RequestByDay[];
  requestsByProduct: RequestByProduct[];
}

export default function OperationalReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>({});

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: OperationalResponse }>({
    method: Endpoints.GetOperationalReport(
      dateRange.startDate,
      dateRange.endDate
    ).method,
    route: Endpoints.GetOperationalReport(
      dateRange.startDate,
      dateRange.endDate
    ).route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ startDate: start, endDate: end });
  }, [startDate, endDate]);

  useEffect(() => {
    reloadData();
  }, [dateRange]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const summary = reportData?.data?.summary;
  const byDay = reportData?.data?.requestsByDay || [];
  const byProduct = reportData?.data?.requestsByProduct || [];

  const statCards = summary
    ? [
        {
          label: "Total Requests",
          value: summary.totalLoanRequests.toLocaleString(),
          color: "#2563EB",
        },
        {
          label: "Approval Rate",
          value: `${summary.approvalRate.toFixed(2)}%`,
          color: "#10B981",
        },
        {
          label: "Rejection Rate",
          value: `${summary.rejectionRate.toFixed(2)}%`,
          color: "#EF4444",
        },
        {
          label: "Avg Processing Time",
          value: `${summary.averageProcessingTimeHours.toFixed(1)} hrs`,
          color: "#F59E0B",
        },
        {
          label: "Active Users",
          value: `${summary.activeUsers.toLocaleString()} / ${summary.totalUsers.toLocaleString()}`,
          color: "#059669",
        },
        {
          label: "User Activity Rate",
          value: `${summary.userActivityRate.toFixed(2)}%`,
          color: "#0D9488",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <ThemeIcon size={48} radius="md" style={{ background: "#2563EB" }}>
              <IconSettings size={24} color="white" />
            </ThemeIcon>
            <div>
              <Title order={2} fw={700}>
                Operational Report
              </Title>
              <Text size="sm" c="dimmed">
                Track loan processing metrics, approval rates, and user activity
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
              Export
            </Button>
          </Group>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            <Grid gutter="md">
              {statCards.map((card, index) => (
                <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ height: "100%" }}>
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                          {card.label}
                        </Text>
                        <Text size="xl" fw={700} style={{ color: card.color }}>
                          {card.value}
                        </Text>
                      </Stack>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>

            {/* User Status Breakdown */}
            <Paper shadow="sm" p="lg" radius="md" withBorder>
              <Title order={4} mb="md">
                <Group gap="xs">
                  <IconUsers size={20} />
                  User Status Breakdown
                </Group>
              </Title>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Stack gap="xs" align="center" p="md" style={{ background: "#F0FDF4", borderRadius: "8px" }}>
                    <Text size="sm" c="dimmed">Active</Text>
                    <Text size="xl" fw={700} c="#059669">
                      {summary.activeUsers.toLocaleString()}
                    </Text>
                    <Badge color="green" variant="light">
                      {summary.totalUsers > 0 ? ((summary.activeUsers / summary.totalUsers) * 100).toFixed(1) : 0}%
                    </Badge>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Stack gap="xs" align="center" p="md" style={{ background: "#FEF3C7", borderRadius: "8px" }}>
                    <Text size="sm" c="dimmed">Suspended</Text>
                    <Text size="xl" fw={700} c="#F59E0B">
                      {summary.suspendedUsers.toLocaleString()}
                    </Text>
                    <Badge color="yellow" variant="light">
                      {summary.totalUsers > 0 ? ((summary.suspendedUsers / summary.totalUsers) * 100).toFixed(1) : 0}%
                    </Badge>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Stack gap="xs" align="center" p="md" style={{ background: "#FEE2E2", borderRadius: "8px" }}>
                    <Text size="sm" c="dimmed">Deactivated</Text>
                    <Text size="xl" fw={700} c="#EF4444">
                      {summary.deactivatedUsers.toLocaleString()}
                    </Text>
                    <Badge color="red" variant="light">
                      {summary.totalUsers > 0 ? ((summary.deactivatedUsers / summary.totalUsers) * 100).toFixed(1) : 0}%
                    </Badge>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Loan Requests by Day */}
            {byDay.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  <Group gap="xs">
                    <IconCalendar size={20} />
                    Loan Requests by Day
                  </Group>
                </Title>
                <Table.ScrollContainer minWidth={500}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Total</Table.Th>
                        <Table.Th>Approved</Table.Th>
                        <Table.Th>Rejected</Table.Th>
                        <Table.Th>Pending</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byDay.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{new Date(item._id).toLocaleDateString()}</Text>
                          </Table.Td>
                          <Table.Td>{item.count}</Table.Td>
                          <Table.Td>
                            <Badge color="green" variant="light">{item.approved}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="red" variant="light">{item.rejected}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="yellow" variant="light">{item.pending}</Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}

            {/* Loan Requests by Product */}
            {byProduct.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Loan Requests by Product
                </Title>
                <Table.ScrollContainer minWidth={600}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Product</Table.Th>
                        <Table.Th>Total</Table.Th>
                        <Table.Th>Approved</Table.Th>
                        <Table.Th>Rejected</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Total Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byProduct.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{item._id || "Unknown"}</Text>
                          </Table.Td>
                          <Table.Td>{item.count}</Table.Td>
                          <Table.Td>
                            <Badge color="green" variant="light">{item.approved}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="red" variant="light">{item.rejected}</Badge>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(item.totalAmount)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

